// Security Optimizations

// SECURITY: Input sanitization utilities
export const sanitizeInput = {
  // Basic HTML sanitization
  html: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  },

  // SQL injection prevention (for search terms)
  searchTerm: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/['"\\;]/g, '') // Remove dangerous characters
      .trim()
      .substring(0, 100); // Limit length
  },

  // Phone number formatting
  phone: (input) => {
    if (typeof input !== 'string') return '';
    return input.replace(/[^\d+\-\s()]/g, '').trim();
  },

  // Email validation and sanitization
  email: (input) => {
    if (typeof input !== 'string') return '';
    const email = input.toLowerCase().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? email : '';
  },

  // Numeric values
  number: (input, min = 0, max = Number.MAX_SAFE_INTEGER) => {
    const num = parseFloat(input);
    if (isNaN(num)) return min;
    return Math.max(min, Math.min(max, num));
  },

  // File names
  fileName: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/[^a-zA-Z0-9._-]/g, '') // Only allow safe characters
      .substring(0, 255); // Limit length
  }
};

// SECURITY: Rate limiting for client-side operations
class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.requests = new Map();
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  isAllowed(key) {
    const now = Date.now();
    const userRequests = this.requests.get(key) || [];
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      console.warn(`🔧 RATE LIMIT: Blocked request for ${key}`);
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(key, validRequests);
    
    return true;
  }

  reset(key) {
    this.requests.delete(key);
  }
}

// Global rate limiters
export const rateLimiters = {
  search: new RateLimiter(20, 60000), // 20 searches per minute
  fileUpload: new RateLimiter(5, 60000), // 5 uploads per minute
  orderCreation: new RateLimiter(10, 300000), // 10 orders per 5 minutes
  paymentSubmission: new RateLimiter(3, 60000) // 3 payments per minute
};

// SECURITY: Validate user permissions before sensitive operations
export const validatePermissions = {
  // Check if user can perform admin operations
  canPerformAdminAction: (user, coopRole, isAdmin) => {
    if (!user) return false;
    return isAdmin || coopRole === 'admin';
  },

  // Check if user can manage specific store
  canManageStore: (user, storeId, userStoreId, coopRole) => {
    if (!user || !storeId) return false;
    
    // Admin can manage any store
    if (coopRole === 'admin') return true;
    
    // Store manager can only manage their assigned store
    if (userStoreId === storeId) return true;
    
    // Cooperative store managers can manage cooperative stores
    if (coopRole === 'cooperative_store_manager' && storeId === 'eP7vVqzAHkZIH3NHNjsc') {
      return true;
    }
    
    return false;
  },

  // Check if user can access financial data
  canAccessFinancial: (coopRole) => {
    return ['admin', 'treasurer', 'cashier', 'manager', 'general manager'].includes(coopRole);
  },

  // Check if user can process orders
  canProcessOrders: (user, coopRole, packerStores = []) => {
    if (!user) return false;
    
    // Admins and financial staff can process any orders
    if (['admin', 'treasurer', 'cashier'].includes(coopRole)) return true;
    
    // Packers can process orders for their assigned stores
    if (packerStores.length > 0) return true;
    
    return false;
  }
};

// SECURITY: Audit logging for sensitive operations
export const auditLog = {
  log: (action, userId, details = {}) => {
    if (process.env.NODE_ENV === 'development') {
    }
    
    // In production, this would send to a secure logging service
    // For now, we'll just log to console in development
  },

  // Log admin actions
  adminAction: (action, userId, target, details = {}) => {
    auditLog.log('ADMIN_ACTION', userId, {
      action,
      target,
      ...details
    });
  },

  // Log financial operations
  financialOperation: (operation, userId, amount, details = {}) => {
    auditLog.log('FINANCIAL_OPERATION', userId, {
      operation,
      amount,
      ...details
    });
  },

  // Log security events
  securityEvent: (event, userId, details = {}) => {
    auditLog.log('SECURITY_EVENT', userId, {
      event,
      ...details
    });
  }
};

// SECURITY: Environment validation
export const validateEnvironment = () => {
  const warnings = [];
  
  // Check if running in development with production data
  if (process.env.NODE_ENV === 'development') {
    if (window.location.hostname.includes('ppac-web.web.app')) {
      warnings.push('Running development build on production domain');
    }
  }
  
  // Check for insecure protocols
  if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
    warnings.push('Running on insecure HTTP protocol');
  }
  
  // Check for missing security headers (basic check)
  if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
    warnings.push('Missing Content Security Policy header');
  }
  
  // Warnings are collected but not logged to console
  
  return warnings;
};

// Initialize security optimizations
export const initializeSecurityOptimizations = () => {
  validateEnvironment();
};
